import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

/*
 * Rare Find predictive search.
 *
 * Shopify's predictive engine treats a short word as a whole token: "ben"
 * returns Ben Nevis but not Benrinnes. Adding "*" to the last word asks it for
 * real prefix matches ("ben*"). Results come back as rendered HTML from
 * sections/predictive-search.liquid, which re-ranks them so proper matches lead.
 */

const RESULTS_SECTION = 'predictive-search';
const EMPTY_SECTION = 'predictive-search-empty';
const PRODUCT_FIELDS = 'title,product_type,vendor,tag,variants.sku';

/** Common misspellings, rewritten before searching. Keys are lowercase. */
const ALIASES = {
  bunahabhain: 'bunnahabhain',
  bunnahabain: 'bunnahabhain',
  bunnahavain: 'bunnahabhain',
  laphroig: 'laphroaig',
  laproaig: 'laphroaig',
  'caol isla': 'caol ila',
  caolila: 'caol ila',
  glenlivit: 'glenlivet',
  glenlivett: 'glenlivet',
  glenfidich: 'glenfiddich',
  glenfiddick: 'glenfiddich',
  bruichladich: 'bruichladdich',
  bruichlladdich: 'bruichladdich',
  macallen: 'macallan',
  ardbeck: 'ardbeg',
  lagavullin: 'lagavulin',
  glenmorangy: 'glenmorangie',
  benrinnis: 'benrinnes',
  benrines: 'benrinnes',
  'ben nevi': 'ben nevis',
  whiskey: 'whisky',
};

/**
 * @param {string} query
 * @returns {{ query: string, rewritten: boolean }}
 */
function applyAliases(query) {
  let out = ` ${query.toLowerCase().replace(/\s+/g, ' ')} `;
  for (const [wrong, right] of Object.entries(ALIASES)) {
    out = out.replace(new RegExp(` ${wrong} `, 'g'), ` ${right} `);
  }
  out = out.trim();
  return { query: out, rewritten: out !== query.toLowerCase().replace(/\s+/g, ' ').trim() };
}

/** @param {string} query */
function withPrefix(query) {
  return /[*"]$/.test(query) ? query : `${query}*`;
}

/** @param {string} value */
function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput
 * @property {HTMLElement} predictiveSearchResults
 * @property {HTMLButtonElement} resetButton
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();
  /** @type {AbortController | null} */
  #activeFetch = null;
  /** @type {Map<string, Promise<string>>} */
  #cache = new Map();
  #emptyMarkup = '';
  #token = 0;
  #index = -1;

  get dialog() {
    return /** @type {import('./dialog').DialogComponent | null} */ (this.closest('dialog-component'));
  }

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#controller;
    const { dialog } = this;

    if (dialog) {
      document.addEventListener('keydown', this.#handleShortcut, { signal });
      document.addEventListener('click', this.#handleOpenerClick, { signal, capture: true });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleOpen, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleClose, { signal });
      this.addEventListener('click', this.#handleBodyClick, { signal });
    }

    this.addEventListener('pointermove', this.#handlePointer, { signal, passive: true });

    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1200));
    idle(() => this.#loadEmptyState());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /* ---------- opening and closing ---------- */

  /** Measures the visible bottom of the header group so the panel sits flush under it. */
  #measureTop() {
    const dialog = this.dialog;
    if (!dialog) return;
    let bottom = 0;
    for (const el of document.querySelectorAll('.shopify-section-group-header-group')) {
      const rect = el.getBoundingClientRect();
      if (rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight / 2) {
        bottom = Math.max(bottom, rect.bottom);
      }
    }
    dialog.style.setProperty('--rf-ps-top', `${Math.round(bottom)}px`);
  }

  /**
   * iOS only opens the keyboard when focus happens inside the tap itself, and the
   * dialog opens a frame later. Focus a throwaway input now and hand focus to the
   * real one once the dialog is open.
   * @param {MouseEvent} event
   */
  #handleOpenerClick = (event) => {
    const target = /** @type {Element} */ (event.target);
    if (!target?.closest?.('.search-action')) return;
    this.#measureTop();
    if (!window.matchMedia('(pointer: coarse)').matches) return;
    const proxy = document.createElement('input');
    proxy.setAttribute('aria-hidden', 'true');
    proxy.tabIndex = -1;
    proxy.style.cssText = 'position:fixed;top:0;left:0;opacity:0;height:0;font-size:16px;pointer-events:none;';
    document.body.append(proxy);
    proxy.focus({ preventScroll: true });
    setTimeout(() => proxy.remove(), 1000);
  };

  /** @param {KeyboardEvent} event */
  #handleShortcut = (event) => {
    const isSlash = event.key === '/' && !(event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement);
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      this.#measureTop();
      this.dialog?.toggleDialog();
    } else if (isSlash && !this.dialog?.refs.dialog.open) {
      event.preventDefault();
      this.#measureTop();
      this.dialog?.showDialog();
    }
  };

  #handleOpen = () => {
    this.#measureTop();
    const { searchInput } = this.refs;
    searchInput.focus({ preventScroll: true });
    if (searchInput.value) searchInput.select();
    this.#loadEmptyState();
  };

  #handleClose = () => {
    this.#clear(false);
  };

  /** Keep focus in the input when tapping blank space in the panel. @param {MouseEvent} event */
  #handleBodyClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.closest('[data-rf-ps-clear-recent]')) {
      event.preventDefault();
      RecentlyViewed.clearProducts();
      this.querySelector('[data-rf-ps-recent-slot]')?.setAttribute('hidden', '');
      this.querySelectorAll('[data-rf-ps-recent-row]').forEach((row) => row.remove());
      this.refs.searchInput.focus();
      return;
    }
    if (!target.closest('a, button, input') && window.matchMedia('(pointer: fine)').matches) {
      this.refs.searchInput.focus({ preventScroll: true });
    }
  };

  /* ---------- fetching ---------- */

  /**
   * @param {string} url
   * @param {AbortSignal} signal
   * @returns {Promise<string>}
   */
  #fetchText(url, signal) {
    const cached = this.#cache.get(url);
    if (cached) return cached;
    const promise = fetch(url, { signal }).then((response) => {
      if (!response.ok) throw new Error(`Search request failed: ${response.status}`);
      return response.text();
    });
    promise.catch(() => this.#cache.delete(url));
    this.#cache.set(url, promise);
    return promise;
  }

  /**
   * @param {string} query
   * @param {AbortSignal} signal
   * @returns {Promise<HTMLElement | null>}
   */
  async #fetchResults(query, signal) {
    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', withPrefix(query));
    url.searchParams.set('section_id', RESULTS_SECTION);
    url.searchParams.set('resources[type]', 'product,collection,page,query');
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    url.searchParams.set('resources[options][fields]', PRODUCT_FIELDS);
    const html = await this.#fetchText(url.toString(), signal);
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector('[data-rf-ps-results]');
  }

  /**
   * @param {string} query
   * @param {AbortSignal} signal
   * @returns {Promise<number | null>}
   */
  async #fetchCount(query, signal) {
    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('options[prefix]', 'last');
    url.searchParams.set('section_id', RESULTS_SECTION);
    const html = await this.#fetchText(url.toString(), signal);
    const match = html.match(/data-rf-ps-count="(\d+)"/);
    return match ? Number(match[1]) : null;
  }

  #newFetch() {
    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();
    this.#token += 1;
    return { signal: this.#activeFetch.signal, token: this.#token };
  }

  /**
   * Debounced input handler.
   */
  search = debounce(() => {
    const typed = this.refs.searchInput.value.replace(/\s+/g, ' ').trim();
    this.refs.resetButton.hidden = typed.length === 0;
    if (!typed) {
      this.#showEmptyState();
      return;
    }
    this.#run(typed).catch((error) => {
      if (error?.name !== 'AbortError') console.error(error);
    });
  }, 120);

  /** @param {string} typed */
  async #run(typed) {
    const { signal, token } = this.#newFetch();
    const { query, rewritten } = applyAliases(typed);
    this.setAttribute('aria-busy', 'true');

    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(new SearchUpdateEvent({ search: { query }, promise: deferred.promise }));

    let effective = query;
    let note = rewritten ? `Showing results for “${query}”` : '';
    let root = await this.#fetchResults(query, signal);

    // Nothing matched properly: try the first few letters of the last word.
    if (root && Number(root.dataset.rfPsStrong) === 0) {
      const words = query.split(' ');
      const last = words[words.length - 1] ?? '';
      const keep = last.length >= 5 ? 4 : last.length === 4 ? 3 : 0;
      if (keep) {
        const shorter = [...words.slice(0, -1), last.slice(0, keep)].join(' ');
        const retry = await this.#fetchResults(shorter, signal);
        if (retry && Number(retry.dataset.rfPsStrong) > 0) {
          root = retry;
          effective = shorter;
          note = `Showing results for “${shorter}…”`;
        }
      }
    }

    if (token !== this.#token || !root) {
      deferred.resolve({ totalCount: 0 });
      return;
    }

    this.#render(root);
    const noteEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-rf-ps-note]'));
    if (noteEl && note) {
      noteEl.textContent = note;
      noteEl.hidden = false;
    }
    this.#highlight(effective);
    this.removeAttribute('aria-busy');
    this.refs.searchInput.setAttribute('aria-expanded', 'true');
    deferred.resolve({ totalCount: Number(root.dataset.rfPsProducts) || 0 });

    this.#fetchCount(effective, signal)
      .then((count) => {
        if (token !== this.#token || !count) return;
        const total = this.querySelector('[data-rf-ps-total]');
        if (total) total.textContent = `${count} `;
      })
      .catch(() => {});
  }

  /** @param {HTMLElement} root */
  #render(root) {
    const wrapper = document.createElement('div');
    wrapper.append(root);
    morph(this.refs.predictiveSearchResults, wrapper);
    this.#select(-1);
    this.refs.predictiveSearchResults.scrollTop = 0;
  }

  /* ---------- empty state ---------- */

  async #loadEmptyState() {
    if (this.#emptyMarkup) return;
    try {
      const url = new URL(location.pathname, location.origin);
      url.searchParams.set('section_id', EMPTY_SECTION);
      const html = await this.#fetchText(url.toString(), this.#controller.signal);
      const root = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-rf-ps-empty]');
      if (!root) return;
      this.#emptyMarkup = root.outerHTML;
      if (!this.refs.searchInput.value.trim()) this.#showEmptyState();
    } catch (error) {
      this.#cache.clear();
    }
  }

  async #showEmptyState() {
    this.#newFetch();
    this.refs.searchInput.setAttribute('aria-expanded', 'false');
    this.removeAttribute('aria-busy');
    if (!this.#emptyMarkup) return;
    const root = /** @type {HTMLElement} */ (
      new DOMParser().parseFromString(this.#emptyMarkup, 'text/html').body.firstElementChild
    );
    this.#render(root);
    await this.#loadRecentlyViewed();
  }

  async #loadRecentlyViewed() {
    const ids = RecentlyViewed.getProducts().slice(0, 3);
    const slot = this.querySelector('[data-rf-ps-recent-slot]');
    if (!ids.length || !slot) return;
    const { signal, token } = this.#newFetch();
    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', ids.map((/** @type {string} */ id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('section_id', RESULTS_SECTION);
    try {
      const html = await this.#fetchText(url.toString(), signal);
      if (token !== this.#token) return;
      const list = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-rf-ps-recent]');
      if (!list || !list.children.length) return;
      list.querySelectorAll('li').forEach((li) => li.setAttribute('data-rf-ps-recent-row', ''));
      list.classList.add('rf-ps-list', 'rf-ps-list--products');
      slot.append(list);
      slot.removeAttribute('hidden');
    } catch (error) {
      /* recently viewed is optional */
    }
  }

  /* ---------- clearing ---------- */

  clearSearch = () => {
    this.#clear(true);
  };

  /** Kept for any Horizon markup that still calls resetSearch. */
  resetSearch = () => {
    this.#clear(true);
  };

  /** @param {boolean} keepFocus */
  #clear(keepFocus) {
    const { searchInput, resetButton } = this.refs;
    searchInput.value = '';
    resetButton.hidden = true;
    this.search.cancel?.();
    this.#showEmptyState();
    if (keepFocus) searchInput.focus();
  }

  /* ---------- keyboard ---------- */

  get #items() {
    return /** @type {HTMLElement[]} */ (
      Array.from(this.refs.predictiveSearchResults.querySelectorAll('[data-rf-ps-item]')).filter(
        (el) => el instanceof HTMLElement && el.offsetParent !== null
      )
    );
  }

  /** @param {number} index */
  #select(index) {
    const items = this.#items;
    const { searchInput } = this.refs;
    this.#index = index;
    items.forEach((item, i) => {
      if (!item.id) item.id = `rf-ps-opt-${i}`;
      item.toggleAttribute('data-rf-ps-active', i === index);
      if (item.getAttribute('role') === 'option') item.setAttribute('aria-selected', String(i === index));
    });
    const active = items[index];
    if (active) {
      searchInput.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    } else {
      searchInput.removeAttribute('aria-activedescendant');
    }
  }

  /** Mouse hover moves the same highlight the arrow keys use. @param {PointerEvent} event */
  #handlePointer = (event) => {
    if (event.pointerType !== 'mouse') return;
    const item = /** @type {HTMLElement} */ (event.target).closest?.('[data-rf-ps-item]');
    if (!item) return;
    const index = this.#items.indexOf(/** @type {HTMLElement} */ (item));
    if (index !== this.#index) this.#select(index);
  };

  /** @param {KeyboardEvent} event */
  onSearchKeyDown = (event) => {
    const items = this.#items;
    switch (event.key) {
      case 'ArrowDown':
        if (!items.length) return;
        event.preventDefault();
        this.#select(this.#index < items.length - 1 ? this.#index + 1 : 0);
        break;
      case 'ArrowUp':
        if (!items.length) return;
        event.preventDefault();
        this.#select(this.#index > 0 ? this.#index - 1 : items.length - 1);
        break;
      case 'Enter': {
        event.preventDefault();
        const active = items[this.#index];
        const link = active?.matches('a') ? active : active?.querySelector('a');
        if (link instanceof HTMLAnchorElement) {
          window.location.href = link.href;
          return;
        }
        this.#submit();
        break;
      }
    }
  };

  /** @param {SubmitEvent} event */
  onSubmit = (event) => {
    event.preventDefault();
    this.#submit();
  };

  #submit() {
    const typed = this.refs.searchInput.value.trim();
    if (!typed) return;
    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', applyAliases(typed).query);
    url.searchParams.set('options[prefix]', 'last');
    window.location.href = url.toString();
  }

  /* ---------- highlighting ---------- */

  /** Wraps word-prefix matches of the query in <mark>. @param {string} query */
  #highlight(query) {
    const words = query
      .toLowerCase()
      .split(/[\s-]+/)
      .filter(Boolean)
      .sort((a, b) => b.length - a.length)
      .map(escapeRegExp);
    if (!words.length) return;
    const pattern = new RegExp(`(^|[\\s\\-(/'’])(${words.join('|')})`, 'gi');
    for (const el of this.querySelectorAll('[data-rf-ps-hl]')) {
      const text = el.textContent ?? '';
      if (!pattern.test(text)) continue;
      pattern.lastIndex = 0;
      const frag = document.createDocumentFragment();
      let last = 0;
      text.replace(pattern, (match, lead, word, offset) => {
        const start = offset + lead.length;
        frag.append(text.slice(last, start));
        const mark = document.createElement('mark');
        mark.textContent = word;
        frag.append(mark);
        last = start + word.length;
        return match;
      });
      frag.append(text.slice(last));
      el.replaceChildren(frag);
    }
  }
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
